import React from 'react';
import { MASTER_READING_PAGES } from './constants';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import withShipment from '../../../withShipment';
import { statusSelector } from '../../../redux/selectors/global';
import { clearStatus } from '../../../redux/actions/global';
import { userInfoSelector } from '../../../redux/selectors/auth';
import MasterReadingTable from './MasterReadingTable';
import {
  allSessionIdsSelector,
  differenceListSelector,
  employeeTicketsSelector,
  readingSelector,
  readingsSelector,
} from '../../../redux/selectors/settings';
import {
  clearReading,
  listMatchTickets,
  listReading,
  listSessionIds,
} from '../../../redux/actions/settings';
import MatchTicketsTable from './MatchTicketsTable';
import Utils from '../../Utils/Utils';
import ReadingSessionAuditLog from '../../audit/ReadingSessionAuditLog';

class MasterReading extends React.Component {
  constructor(props) {
    super(props);
    // When opened from the Weekly Report's Daily Analysis → Open Match Tickets,
    // deep-link straight into that day's Match Tickets view (nav state carries
    // the reading session id and the report state to return to).
    const nav = props.location?.state || {};
    this.state = {
      currentPage: nav.openMatchTickets
        ? MASTER_READING_PAGES.MATCH_TICKETS
        : MASTER_READING_PAGES.TABLE,
      feedbackBarOpen: false,
      error: false,
      selectedDate: new Date(),
      fromDate: Utils.formatTodayDate(
        new Date(new Date().setDate(new Date().getDate() - 7))
      ),
      toDate: Utils.formatTodayDate(new Date()),
      selectedSessionId: nav.sessionId || null,
    };
  }

  // Back out of the deep-linked Match Tickets view to the exact Weekly Report
  // the user came from (week/day/tab preserved via `restoreReport`).
  handleExitToReport = () => {
    const restore = this.props.location?.state?.restoreReport;
    if (this.props.history) {
      this.props.history.push(
        '/report',
        restore ? { restoreReport: restore } : undefined
      );
    }
  };

  componentDidMount() {
    this.props.clearReading();
    this.props.listSessionIds(
      this.state.fromDate,
      this.state.toDate,
      this.props.userInfo.selectedLocationId
    );
  }

  handleFromDateChange = (date) => {
    this.setState({ fromDate: Utils.formatTodayDate(date) });
  };

  handleToDateChange = (date) => {
    this.setState({ toDate: Utils.formatTodayDate(date) });
  };

  clearSessionId = () => {
    this.setState({ selectedSessionId: null });
  };

  handleSessionChange = (event) => {
    this.setState({ selectedSessionId: event.target.value }, () => {
      this.refreshComponent(this.state.selectedSessionId);
    });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar();
    }
  }

  refreshComponent = (sessionId) => {
    this.props.listReading(sessionId, this.props.userInfo.selectedLocationId);
  };

  setCurrentPage = (newPage) => {
    this.setState({
      currentPage: newPage,
    });
  };

  displayMatchTickets = () => {
    return (
      <MatchTicketsTable
        userInfo={this.props.userInfo}
        listMatchTickets={this.props.listMatchTickets}
        differenceList={this.props.differenceList}
        employeeTickets={this.props.employeeTickets}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        date={this.state.selectedDate}
        fromDate={this.state.fromDate}
        toDate={this.state.toDate}
        selectedSessionId={this.state.selectedSessionId}
        onExit={
          this.props.location?.state?.restoreReport
            ? this.handleExitToReport
            : undefined
        }
      />
    );
  };

  displayTable = () => {
    return (
      <MasterReadingTable
        userInfo={this.props.userInfo}
        refreshComponent={this.refreshComponent}
        readings={this.props.readings}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        fromDate={this.state.fromDate}
        toDate={this.state.toDate}
        handleFromDateChange={this.handleFromDateChange}
        handleToDateChange={this.handleToDateChange}
        listSessionIds={this.props.listSessionIds}
        locationId={this.props.userInfo.selectedLocationId}
        allSessionIds={this.props.allSessionIds}
        selectedSessionId={this.state.selectedSessionId}
        handleSessionChange={this.handleSessionChange}
        clearSessionId={this.clearSessionId}
        clearReading={this.props.clearReading}
      />
    );
  };

  displayAuditLog = () => {
    return (
      <ReadingSessionAuditLog
        embeddedSessionId={this.state.selectedSessionId}
        onBack={() => this.setCurrentPage(MASTER_READING_PAGES.TABLE)}
      />
    );
  };

  displayCurrentPage = () => {
    switch (this.state.currentPage) {
      case MASTER_READING_PAGES.TABLE:
        return this.displayTable();
      case MASTER_READING_PAGES.MATCH_TICKETS:
        return this.displayMatchTickets();
      case MASTER_READING_PAGES.AUDIT_LOG:
        return this.displayAuditLog();
      default:
        return <div>We encountered an error. Please refresh the page.</div>;
    }
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  render() {
    return (
      <React.Fragment>
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
        {this.displayCurrentPage()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  status: statusSelector(state),
  reading: readingSelector(state),
  readings: readingsSelector(state),
  differenceList: differenceListSelector(state),
  employeeTickets: employeeTicketsSelector(state),
  allSessionIds: allSessionIdsSelector(state),
});

const actionCreators = {
  clearStatus,
  listReading,
  listMatchTickets,
  listSessionIds,
  clearReading,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  MasterReading
);
